import React from "react";
import { DateTime } from "luxon";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";

import { THEME_SETTINGS } from "@theme-settings";
import { BetSlipShare } from "@models";
import { Button } from "@components/buttons";
import { useRootAppStore } from "@hooks";
import { getCurrentCulture } from "@utils";
import { SharedBetSlipActionsTypeEnum } from "@data-types/analytics-tracker/QuickPlayInteractionTypeEnum";
import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";
import SharedSlipEventOfferGroups from "@features/my-bets/mobile/bet-slip-shared-page/SharedSlipEventOfferGroups";
import { analyticsTracker } from "@utils/analytics";
import { useFixBody } from "@state/hooks/shared";

export default observer(function ChatBetSlipSharedPopup() {
	const { chatViewStore } = useRootAppStore();

	useFixBody(true, !!chatViewStore.chatBetSlipShareData);

	if (!THEME_SETTINGS.features.isBetSlipShareEnabled) {
		return null;
	}

	if (!chatViewStore.chatBetSlipShareData) {
		return null;
	}

	return (
		<ErrorBoundaryWithPropagation
			componentName={"Mobile/ChatBetSlipSharedPopup.tsx"}
		>
			<div className="popup popup--sbs">
				<PopupContent bet={chatViewStore.chatBetSlipShareData} />
			</div>
		</ErrorBoundaryWithPropagation>
	);
});

const PopupContent = observer(function PopupContent(props: {
	bet: BetSlipShare;
}) {
	const { chatViewStore } = useRootAppStore();

	return (
		<ErrorBoundaryWithPropagation
			componentName={"Mobile/ChatBetSlipSharedPopup.tsx/PopupContent"}
		>
			<div className="popup__card popup--sbs__card">
				<PopupHeader
					onClose={chatViewStore.chatBetSlipShareHandler.onDispose}
				/>
				<div className="popup__body u-padd--top--reset">
					<PopupInfo bet={props.bet} />
					<div className="popup__ticket bet">
						<PopupBody bet={props.bet} />
					</div>
				</div>
				<PopupFooter bet={props.bet} />
			</div>
		</ErrorBoundaryWithPropagation>
	);
});

export const PopupHeader = observer(function PopupHeader(props: {
	onClose: () => void;
}) {
	const { t } = useTranslation("mybets");

	return (
		<ErrorBoundaryWithPropagation
			componentName={"Mobile/ChatBetSlipSharedPopup.tsx/PopupHeader"}
		>
			<div className="popup__header">
				<div className="popup__title ">{t("SHARE.TITLE")}</div>
				<button
					type="button"
					onClick={props.onClose}
					className="popup__close btn btn--square btn--square--sml btn--primary--inverted"
				>
					<i className="u-icon u-icon--tny u-icon--close u-color--text--secondary" />
				</button>
			</div>
		</ErrorBoundaryWithPropagation>
	);
});

export const PopupInfo = observer(function PopupInfo(props: {
	bet: BetSlipShare;
}) {
	const { t } = useTranslation();

	return (
		<ErrorBoundaryWithPropagation
			componentName={"Mobile/ChatBetSlipSharedPopup.tsx/PopupInfo"}
		>
			<div className="bet__header__inner bet--slip__header__inner u-mar--bottom--xsml">
				<div className="bet__header__col">
					<span className="u-type--xxsml u-color--text--tertiary">
						{t("BET_SLIP.DATE")}:{" "}
					</span>

					<span className="u-type--sml u-color--text--primary">
						{DateTime.fromISO(props.bet.dateCreated).toLocaleString(
							DateTime.DATETIME_SHORT
						)}
					</span>
				</div>

				<div className="bet__header__col">
					<span className="u-type--xxsml u-color--text--tertiary">
						{t("BET_SLIP.MAX_COEF")}:{" "}
					</span>

					<span className="u-type--sml u-color--text--primary">
						{props.bet.maxCoefficient.toFixed(2)}
					</span>
				</div>
			</div>
		</ErrorBoundaryWithPropagation>
	);
});

export const PopupBody = observer(function PopupBody(props: {
	bet: BetSlipShare;
}) {
	return (
		<ErrorBoundaryWithPropagation
			componentName={"Mobile/ChatBetSlipSharedPopup.tsx/PopupBody"}
		>
			<SharedSlipEventOfferGroups
				betSlipShare={props.bet}
				groupHasTitle
				offersHaveColor
				titleHasScore
			/>
		</ErrorBoundaryWithPropagation>
	);
});

const PopupFooter = observer(function PopupFooter(props: {
	bet: BetSlipShare;
}) {
	const { chatViewStore } = useRootAppStore();
	const { t } = useTranslation("mybets");
	const { bet } = props;

	return (
		<ErrorBoundaryWithPropagation
			componentName={"Mobile/ChatBetSlipSharedPopup.tsx/PopupFooter"}
		>
			<div className="popup__footer popup__footer--bordered">
				<Button
					type="button"
					className="btn--base btn--block btn--primary--inverted popup__btn popup__btn--secondary"
					onClick={() => {
						analyticsTracker.trackQuickPlayInteraction(
							SharedBetSlipActionsTypeEnum.PageVisit
						);
						chatViewStore.chatBetSlipShareHandler.onDispose();
						chatViewStore.toggleChatBox(false);
						App.state.redirect(
							`/${getCurrentCulture()}/quick-play/shared-bet-slips`
						);
					}}
					btnText={t("SHARE.SEE_ALL")}
				/>
				<Button
					type="button"
					className="btn--base btn--block btn--accent popup__btn popup__btn--primary"
					onClick={() =>
						chatViewStore.chatBetSlipShareHandler.addToSlip(bet)
					}
					btnText={t("SHARE.ADD_TO_SLIP")}
				/>
			</div>
		</ErrorBoundaryWithPropagation>
	);
});
